import { getDefaultBridgeUrl } from './api';
import { checkCibdStatus } from './cibdClient';
import { loadJsPdf } from './jsPdf';
import {
  PRINTER_SETTINGS_KEY,
  applyCibdStationBindings,
  getTenantSettingsSession,
  getTenantStorageKey,
  isCibdStationConnectionCurrent,
  readTenantLocalJson,
  resolveCibdStationConnection,
} from './tenantSettingsStorage';
import { DEFAULT_PRINT_POINT_ID, normalizePrintPoints } from './printPoints';

const money = (value) => Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const roundMoney = (value) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

export const buildSalonConferenceRows = ({ session = {}, totals = {}, includeServiceFee = false } = {}) => {
  const items = (session.items || []).filter((item) => item?.status !== 'cancelled');
  const rows = [
    { type: 'title', text: 'CONFERÊNCIA DA MESA' },
    { type: 'text', text: 'NÃO É COMPROVANTE FISCAL', align: 'center' },
    { type: 'text', text: `Mesa ${session.tableNumber || '-'} • Comanda #${session.id || '-'}` },
    { type: 'text', text: new Date().toLocaleString('pt-BR') },
    { type: 'divider' },
  ];
  items.forEach((item) => {
    const quantity = Number(item.quantity || 0);
    const unitPrice = Number(item.unitPrice || (quantity ? Number(item.totalPrice || 0) / quantity : 0));
    rows.push({
      type: 'money',
      left: `${quantity}x ${item.name || 'Item'} (${money(unitPrice)} cada)`,
      right: money(item.totalPrice),
    });
    (item.options || []).forEach((option) => {
      rows.push({ type: 'text', text: `  + ${Number(option.quantity || 1)}x ${option.name || 'Opção'}`, small: true });
    });
    if (item.observations) rows.push({ type: 'text', text: `  Obs: ${item.observations}`, small: true });
  });
  rows.push({ type: 'divider' });
  rows.push({ type: 'money', left: 'Subtotal', right: money(totals.subtotal) });
  if (Number(totals.discount || 0) > 0) rows.push({ type: 'money', left: 'Desconto', right: `- ${money(totals.discount)}` });
  if (includeServiceFee) rows.push({ type: 'money', left: 'Taxa de serviço (10%)', right: money(totals.serviceFee) });
  rows.push({ type: 'money', left: 'TOTAL', right: money(totals.totalDue), bold: true });
  return rows;
};

const buildSalonConferencePdfDocument = (input, JsPdf, requestedPaperWidthMm = 80) => {
  if (typeof JsPdf !== 'function') throw new Error('Gerador de PDF indisponível.');
  const rows = buildSalonConferenceRows(input);
  const width = Number(requestedPaperWidthMm) === 58 ? 58 : 80;
  const margin = width === 58 ? 5 : 4;
  const contentWidth = width - margin * 2;
  const temp = new JsPdf({ unit: 'mm', format: [width, 240] });
  let estimatedHeight = 8;
  rows.forEach((row) => {
    if (row.type === 'divider') { estimatedHeight += 3; return; }
    const size = row.type === 'title' ? 15 : row.small ? 9 : row.bold ? 13 : 11;
    temp.setFontSize(size);
    const leftWidth = row.type === 'money' ? contentWidth * 0.65 : contentWidth;
    const text = row.type === 'money' ? row.left : row.text;
    estimatedHeight += Math.max(1, temp.splitTextToSize(String(text || ''), leftWidth).length) * size * 0.43 + 1;
  });
  const paperHeight = Math.max(70, estimatedHeight + 8);
  const doc = new JsPdf({
    orientation: paperHeight >= width ? 'portrait' : 'landscape',
    unit: 'mm',
    format: [width, paperHeight],
  });
  let y = 6;
  rows.forEach((row) => {
    if (row.type === 'divider') {
      y += 1;
      doc.setDrawColor(130);
      doc.line(margin, y, width - margin, y);
      y += 3;
      return;
    }
    const size = row.type === 'title' ? 15 : row.small ? 9 : row.bold ? 13 : 11;
    const bold = row.type === 'title' || row.bold;
    doc.setFont('courier', bold ? 'bold' : 'normal');
    doc.setFontSize(size);
    if (row.type === 'money') {
      const leftWidth = contentWidth * 0.64;
      const lines = doc.splitTextToSize(String(row.left || ''), leftWidth);
      lines.forEach((line, index) => {
        doc.text(line, margin, y);
        if (index === 0) doc.text(String(row.right || ''), width - margin, y, { align: 'right' });
        y += size * 0.43;
      });
      y += 1;
      return;
    }
    const lines = doc.splitTextToSize(String(row.text || ''), contentWidth);
    lines.forEach((line) => {
      doc.text(line, row.align === 'center' || row.type === 'title' ? width / 2 : margin, y, {
        align: row.align === 'center' || row.type === 'title' ? 'center' : 'left',
      });
      y += size * 0.43;
    });
    y += 1;
  });
  return {
    pdfBase64: doc.output('datauristring').split(',')[1] || '',
    pdfPaper: {
      widthMm: width,
      heightMm: Number(paperHeight.toFixed(2)),
    },
  };
};

export const buildSalonConferencePdfBase64 = (input, JsPdf) => (
  buildSalonConferencePdfDocument(input, JsPdf).pdfBase64
);

const projectConferenceItem = (item = {}) => ({
  id: item.id,
  name: item.name || 'Item',
  quantity: Number(item.quantity || 0),
  unitPrice: roundMoney(item.unitPrice),
  totalPrice: roundMoney(item.totalPrice),
  observations: item.observations || '',
  options: (item.options || []).map((option) => ({
    name: option.name || 'Opção',
    quantity: Number(option.quantity || 1),
    unitPrice: roundMoney(option.unitPrice),
    totalPrice: roundMoney(option.totalPrice),
  })),
});

export const printSalonConference = async ({ session, totals, includeServiceFee, authSession = getTenantSettingsSession() }) => {
  let connection = null;
  const assertCurrentSession = () => {
    if (!getTenantStorageKey(PRINTER_SETTINGS_KEY, authSession)) {
      throw new Error('Sessão alterada. Entre novamente antes de imprimir a conferência.');
    }
    if (connection && !isCibdStationConnectionCurrent(connection, {
      baseUrl: getDefaultBridgeUrl(),
      session: authSession,
    })) {
      throw new Error('Conexão do CIBD alterada. Tente imprimir novamente.');
    }
  };
  assertCurrentSession();
  const tenantSettings = readTenantLocalJson(PRINTER_SETTINGS_KEY, {}, authSession);
  connection = resolveCibdStationConnection({
    baseUrl: getDefaultBridgeUrl(),
    session: authSession,
    tenantSettings,
  });
  assertCurrentSession();
  const settings = applyCibdStationBindings(tenantSettings, connection);
  const caixa = normalizePrintPoints(settings).find((point) => point.id === DEFAULT_PRINT_POINT_ID);
  if (!caixa?.printerId) throw new Error('Selecione a impressora do ponto Caixa nas configurações de impressão.');
  const baseUrl = connection?.baseUrl || getDefaultBridgeUrl();
  const cibdStatus = await checkCibdStatus({ baseUrl });
  assertCurrentSession();
  const JsPdf = await loadJsPdf();
  assertCurrentSession();
  const { pdfBase64, pdfPaper } = buildSalonConferencePdfDocument(
    { session, totals, includeServiceFee },
    JsPdf,
    cibdStatus?.paperWidthMm,
  );
  const token = String(connection ? connection.token : settings.cibdToken || settings.bridgeToken || '').trim();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = /^bearer\s+/i.test(token) ? token : `Bearer ${token}`;
  assertCurrentSession();
  let response;
  try {
    response = await fetch(`${baseUrl}/api/system/print/comanda`, {
      method: 'POST',
      headers,
      credentials: 'omit',
      body: JSON.stringify({
        printerId: caixa.printerId,
        pdfBase64,
        pdfPaper,
        forceExternalPdf: true,
        order: {
          id: `CONFERENCIA-MESA-${session.id}`,
          salesChannel: 'Salão • Conferência não fiscal',
          createdAt: Date.now(),
          customer: { name: session.customerName || `Mesa ${session.tableNumber}` },
          items: (session.items || [])
            .filter((item) => item?.status !== 'cancelled')
            .map(projectConferenceItem),
          total: roundMoney(totals.totalDue),
        },
      }),
    });
  } catch {
    throw new Error('Não foi possível acessar o CIBD local. Verifique se o conector está aberto e se esta origem está autorizada.');
  }
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Falha ao imprimir a conferência no ponto Caixa.');
  }
  return response.json().catch(() => ({ success: true }));
};
